import React, { useCallback, useEffect, useState } from 'react';
import Ionicons from '@expo/vector-icons/Ionicons';
import { StyleSheet, Text, TouchableOpacity, View } from './rn';
import AppSheet from './AppSheet';
import ActivityIndicator from './LoadingIndicator';
import SafetyQrCode from './SafetyQrCode';
import { InfoButton, InfoText, useInfo } from './InfoHint';
import { fontSize, radius, spacing } from '../theme';
import { useTheme } from '../theme/ThemeContext';
import { useTranslation } from '../i18n/I18nContext';
import {
  clearVerified,
  isVerified,
  loadSafetyNumber,
  markVerified,
  safetyGroups,
  safetyQrPayload,
  verificationFor,
  type SafetyState,
  type Verification,
} from '../e2ee/safetyNumber';

type Props = {
  visible: boolean;
  token: string;
  myId: number;
  peerId: number;
  peerName: string;
  onClose: () => void;
  /** Ekran kapanırken son durum: başlıktaki ve profildeki işaret buna göre güncellenir. */
  onVerifiedChange?: (verified: boolean) => void;
  /** Testte gerçek dizin/depo yerine sahtesi geçilebilsin diye. */
  deps?: {
    load?: (token: string, myId: number, peerId: number) => Promise<SafetyState>;
    read?: (peerId: number) => Promise<Verification | null>;
    mark?: (peerId: number, digits: string) => Promise<void>;
    clear?: (peerId: number) => Promise<void>;
  };
};

/**
 * Güvenlik numarası ekranı (9.34.3 · D-696).
 *
 * İki kişi numarayı ya da QR'ı karşılaştırır; aynıysa araya kimse girmemiştir.
 * "Doğrulandı" yalnız bu cihazda saklanır ve DOĞRULANAN NUMARA olarak tutulur:
 * biri yeni bir cihaz ekleyip numara değişince işaret kendiliğinden düşer.
 * Mesajlaşmayı hiçbir durumda engellemez. QR'ı kamerayla okutmak yeni uygulama
 * sürümüyle gelecek; o zamana kadar numara karşılaştırılıp düğmeye basılır.
 */
export default function SafetyNumberSheet({ visible, token, myId, peerId, peerName, onClose, onVerifiedChange, deps }: Props) {
  const colors = useTheme();
  const { t } = useTranslation();
  const info = useInfo();
  const styles = React.useMemo(() => makeStyles(colors), [colors]);
  const load = deps?.load ?? loadSafetyNumber;
  const read = deps?.read ?? verificationFor;
  const mark = deps?.mark ?? markVerified;
  const clear = deps?.clear ?? clearVerified;

  const [safety, setSafety] = useState<SafetyState | null>(null);
  const [record, setRecord] = useState<Verification | null>(null);
  const [busy, setBusy] = useState(false);

  const yukle = useCallback(async () => {
    setSafety(null);
    const [state, saved] = await Promise.all([
      load(token, myId, peerId).catch((): SafetyState => ({ state: 'error' })),
      read(peerId).catch(() => null),
    ]);
    setSafety(state);
    setRecord(saved);
  }, [load, read, token, myId, peerId]);

  useEffect(() => {
    if (visible) yukle();
  }, [visible, yukle]);

  const digits = safety?.state === 'ready' ? safety.digits : null;
  const verified = isVerified(record, digits);

  const kapat = useCallback(() => {
    onVerifiedChange?.(verified);
    onClose();
  }, [onClose, onVerifiedChange, verified]);

  const dogrula = useCallback(async () => {
    if (!digits) return;
    setBusy(true);
    try {
      await mark(peerId, digits);
      setRecord({ number: digits, at: new Date().toISOString() });
    } finally {
      setBusy(false);
    }
  }, [digits, mark, peerId]);

  const kaldir = useCallback(async () => {
    setBusy(true);
    try {
      await clear(peerId);
      setRecord(null);
    } finally {
      setBusy(false);
    }
  }, [clear, peerId]);

  const groups = digits ? safetyGroups(digits) : [];

  return (
    <AppSheet
      visible={visible}
      title={t('safety.title')}
      onClose={kapat}
      testID="safety"
      titleAccessory={<InfoButton open={info.open} onPress={info.toggle} subject={t('safety.title')} testID="safety-info" />}>
            <InfoText open={info.open} text={t('safety.explainInfo')} style={styles.explain} testID="safety-info-text" />

            {safety === null ? (
              <ActivityIndicator testID="safety-loading" color={colors.gray400} />
            ) : safety.state === 'ready' ? (
              <>
                <Text style={styles.compare}>{t('safety.compare', { name: peerName })}</Text>
                <SafetyQrCode
                  testID="safety-qr"
                  value={safetyQrPayload(safety.digits)}
                  size={220}
                  accessibilityLabel={t('safety.qrLabel')}
                />
                <View
                  testID="safety-number"
                  style={styles.number}
                  accessible
                  accessibilityLabel={groups.join(' ')}>
                  {[0, 1, 2].map((line) => (
                    <View key={line} style={styles.numberLine}>
                      {groups.slice(line * 4, line * 4 + 4).map((group, i) => (
                        <Text key={i} style={styles.group} selectable>{group}</Text>
                      ))}
                    </View>
                  ))}
                </View>
                <View style={styles.statusRow} testID="safety-status">
                  {verified && <Ionicons name="checkmark-circle" size={18} color={colors.black} accessible={false} />}
                  <Text style={styles.status}>{verified ? t('safety.verified') : t('safety.notVerified')}</Text>
                </View>
                <TouchableOpacity
                  testID={verified ? 'safety-clear' : 'safety-mark'}
                  style={[verified ? styles.secondary : styles.primary, busy && styles.disabled]}
                  disabled={busy}
                  accessibilityRole="button"
                  onPress={verified ? kaldir : dogrula}>
                  <Text style={verified ? styles.secondaryText : styles.primaryText}>
                    {verified ? t('safety.clearVerified') : t('safety.markVerified')}
                  </Text>
                </TouchableOpacity>
              </>
            ) : (
              <View style={styles.unavailable} testID={`safety-${safety.state}`}>
                <Text style={styles.compare}>
                  {safety.state === 'peer'
                    ? t('safety.peerUnavailable')
                    : safety.state === 'self'
                    ? t('safety.selfUnavailable')
                    : t('safety.loadFailed')}
                </Text>
                {safety.state === 'error' && (
                  <TouchableOpacity testID="safety-retry" style={styles.secondary} accessibilityRole="button" onPress={yukle}>
                    <Text style={styles.secondaryText}>{t('common.retry')}</Text>
                  </TouchableOpacity>
                )}
              </View>
            )}

    </AppSheet>
  );
}

function makeStyles(colors: ReturnType<typeof useTheme>) {
  return StyleSheet.create({
    explain: { fontSize: fontSize.sm, color: colors.gray400, lineHeight: 20 },
    compare: { fontSize: fontSize.sm, color: colors.black, lineHeight: 20 },
    number: { alignSelf: 'center', gap: spacing.xs },
    numberLine: { flexDirection: 'row', gap: spacing.md, justifyContent: 'center' },
    group: {
      fontSize: fontSize.lg,
      color: colors.black,
      fontVariant: ['tabular-nums'],
      letterSpacing: 1,
    },
    statusRow: { flexDirection: 'row', alignItems: 'center', justifyContent: 'center', gap: spacing.xs },
    status: { fontSize: fontSize.md, fontWeight: '600', color: colors.black },
    primary: {
      backgroundColor: colors.black,
      borderRadius: radius.control,
      paddingVertical: spacing.md,
      alignItems: 'center',
    },
    primaryText: { color: colors.white, fontWeight: '600', fontSize: fontSize.md },
    secondary: {
      borderWidth: 1,
      borderColor: colors.gray200,
      borderRadius: radius.control,
      paddingVertical: spacing.md,
      alignItems: 'center',
    },
    secondaryText: { color: colors.black, fontWeight: '600', fontSize: fontSize.md },
    disabled: { opacity: 0.5 },
    unavailable: { gap: spacing.md },
  });
}
